/**
 * Client-side mirror of the Access rules (docs/CONTRACT.md → Auth) for UX only: what to offer and
 * what to explain. The API enforces every rule regardless.
 */
import type { AccessRole, AccessUser, Me } from '@/api/types'

/** The only email domain FPA users can have (ACCESS_ALLOWED_EMAIL_DOMAINS default). */
export const ACCESS_EMAIL_DOMAIN = 'rbialliance.com'

const LOCAL_PART = /^[a-z0-9_%+-]+(?:\.[a-z0-9_%+-]+)*$/

/** An error message for `raw`, or null when it is one well-formed @rbialliance.com address. */
export function companyEmailError(raw: string): string | null {
  const email = raw.trim().toLowerCase()
  if (!email) return 'Enter their company email.'
  const at = email.indexOf('@')
  if (at <= 0 || at !== email.lastIndexOf('@')) return 'Enter one email address, like name@rbialliance.com.'
  const local = email.slice(0, at)
  const domain = email.slice(at + 1)
  if (domain !== ACCESS_EMAIL_DOMAIN) return `Only @${ACCESS_EMAIL_DOMAIN} accounts can be added.`
  if (local.length > 64 || !LOCAL_PART.test(local)) return 'That email address is not valid.'
  return null
}

export const ROLE_LABEL: Record<AccessRole, string> = {
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
}

export const ROLE_HELP: Record<AccessRole, string> = {
  viewer: 'Sees the runway; payroll names are masked.',
  manager: 'Also syncs, sees payroll names and manages viewers and managers.',
  admin: 'Everything, including granting and revoking admin.',
}

/** Roles `me` may hand out. */
export function assignableRoles(me: Me): AccessRole[] {
  return me.permissions.canGrantAdmin ? ['viewer', 'manager', 'admin'] : ['viewer', 'manager']
}

export function isSelf(me: Me, user: AccessUser): boolean {
  return me.email.toLowerCase() === user.email
}

/** Why `me` cannot change or remove `user`, or null when they can. */
export function lockReason(me: Me, user: AccessUser): string | null {
  if (isSelf(me, user)) return 'You cannot change your own access.'
  if (user.role === 'admin' && !me.permissions.canGrantAdmin) return 'Only an admin can change an admin.'
  return null
}
